import { ExpenseCategory, IncomeCategory } from "@boo/shared";
import type {
  CardIssuer,
  ClassifyIncomingRequest,
  MonthlySummaryResponse,
  PaybackCandidate,
  ShareTone,
  SplitGroup,
  SplitGroupMember,
  Transaction,
  SlipParsedDraftDto,
  TransactionSort,
  TransactionSource,
  TransactionType,
} from "@boo/shared";

const INCOME_CATEGORY_VALUES = new Set<string>(Object.values(IncomeCategory));
import { request } from "./apiClient";

export { ApiError } from "./apiClient";

export interface TransactionsResult {
  data: Transaction[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchMonthlySummary(
  year: number,
  month: number,
): Promise<MonthlySummaryResponse> {
  return request<MonthlySummaryResponse>(
    `/transactions/summary/${year}/${month}`,
  );
}

export async function fetchTransactions(params: {
  year: number;
  month: number;
  page?: number;
  limit?: number;
  sort?: TransactionSort;
  title?: string;
  category?: ExpenseCategory | null;
  transactionType?: TransactionType | null;
  source?: TransactionSource | null;
  unassigned?: boolean;
  accountId?: string | null;
}): Promise<TransactionsResult> {
  const { year, month, page = 1, limit = 20 } = params;
  const qs = new URLSearchParams({
    year: String(year),
    month: String(month),
    page: String(page),
    limit: String(limit),
  });
  if (params.sort) qs.set("sort", params.sort);
  const title = params.title?.trim();
  if (title) qs.set("title", title);
  if (params.category) qs.set("category", params.category);
  if (params.transactionType) qs.set("transactionType", params.transactionType);
  if (params.source) qs.set("source", params.source);
  if (params.unassigned) qs.set("unassigned", "true");
  if (params.accountId) qs.set("accountId", params.accountId);
  const result = await request<TransactionsResult>(`/transactions?${qs}`);
  return result;
}

export async function patchTransactionAccount(
  id: string,
  accountId: string | null,
): Promise<Transaction> {
  return request<Transaction>(`/transactions/${id}/account`, {
    method: "PATCH",
    data: { accountId },
  });
}

export async function assignTransactionsAccount(
  transactionIds: string[],
  accountId: string,
): Promise<{ updated: number }> {
  return request<{ updated: number }>("/transactions/account", {
    method: "PATCH",
    data: { transactionIds, accountId },
  });
}

export async function deleteTransaction(id: string): Promise<void> {
  await request<void>(`/transactions/${id}`, { method: "DELETE" });
}

export async function patchTransactionCategory(
  id: string,
  category: ExpenseCategory | IncomeCategory,
): Promise<Transaction> {
  const data = INCOME_CATEGORY_VALUES.has(category)
    ? { incomeCategory: category }
    : { category };
  const result = await request<Transaction>(`/transactions/${id}/category`, {
    method: "PATCH",
    data,
  });
  return result;
}

export async function patchTransactionCardPayment(
  id: string,
  cardPaymentIssuer: CardIssuer | null,
): Promise<Transaction> {
  return request<Transaction>(`/transactions/${id}/card-payment`, {
    method: "PATCH",
    data: { cardPaymentIssuer },
  });
}

export async function fetchPaybackCandidates(
  id: string,
): Promise<PaybackCandidate[]> {
  return request<PaybackCandidate[]>(`/transactions/${id}/payback-candidates`);
}

export async function classifyIncoming(
  id: string,
  payload: ClassifyIncomingRequest,
): Promise<Transaction> {
  return request<Transaction>(`/transactions/${id}/incoming`, {
    method: "PATCH",
    data: payload,
  });
}

export interface UpdateTransactionPayload {
  amount?: number;
  title?: string;
  note?: string;
  date?: string;
}

export async function updateIncomeTransaction(
  id: string,
  payload: UpdateTransactionPayload,
): Promise<Transaction> {
  const result = await request<Transaction>(`/transactions/${id}`, {
    method: "PATCH",
    data: payload,
  });
  return result;
}

export async function triggerBankImport(): Promise<{
  ok: boolean;
  imported: number;
}> {
  return request("/scheduler/bank-import/manual", { method: "POST" });
}

// ── QR slip web upload (JWT) ──────────────────────────────────────────────────

export const MAX_SLIP_BYTES = 5 * 1024 * 1024; // 5 MB
export const SLIP_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

export async function parseSlipWeb(file: File): Promise<SlipParsedDraftDto> {
  const formData = new FormData();
  formData.append("file", file);
  return request<SlipParsedDraftDto>("/transactions/slip/web", {
    method: "POST",
    data: formData,
  });
}

export interface ConfirmSlipPayload {
  amount: number;
  title: string;
  date: string;
  category: ExpenseCategory;
  note: string;
  isTransfer: boolean;
  accountId?: string;
}

export async function confirmSlipWeb(
  payload: ConfirmSlipPayload,
): Promise<Transaction> {
  const result = await request<Transaction>("/transactions/slip/web/confirm", {
    method: "POST",
    data: payload,
  });
  return result;
}

// ── Split Groups ────────────────────────────────────────────────────────────

export interface CreateSplitGroupPayload {
  name: string;
  friendIds?: string[];
}

export interface UpdateSplitGroupPayload {
  name?: string;
  tone?: ShareTone;
  shareMessageOpen?: string | null;
  shareMessageFinal?: string | null;
}

export interface SplitGroupMemberPayload {
  friendId: string;
}

export async function getSplitGroups(): Promise<SplitGroup[]> {
  return request<SplitGroup[]>("/split-groups");
}

export async function createSplitGroup(
  payload: CreateSplitGroupPayload,
): Promise<SplitGroup> {
  return request<SplitGroup>("/split-groups", {
    method: "POST",
    data: payload,
  });
}

export async function updateSplitGroup(
  id: string,
  payload: UpdateSplitGroupPayload,
): Promise<SplitGroup> {
  return request<SplitGroup>(`/split-groups/${id}`, {
    method: "PATCH",
    data: payload,
  });
}

export async function deleteSplitGroup(id: string): Promise<void> {
  await request<void>(`/split-groups/${id}`, { method: "DELETE" });
}

export async function addSplitGroupMember(
  groupId: string,
  payload: SplitGroupMemberPayload,
): Promise<SplitGroupMember> {
  return request<SplitGroupMember>(`/split-groups/${groupId}/members`, {
    method: "POST",
    data: payload,
  });
}

export async function removeSplitGroupMember(
  groupId: string,
  memberId: string,
): Promise<void> {
  await request<void>(`/split-groups/${groupId}/members/${memberId}`, {
    method: "DELETE",
  });
}
